// ETF Wealth Builder Calculator -- the Conversion CTA.
//
// This is where the trap snaps shut: we extract the mathematical variance
// between a pessimistic sequence outcome and the median, and the inflation
// gap, turning abstract quant concepts into a glaring financial threat the
// visitor wants to fix -- then point them at the Forge Ultra platform.

import React from 'react';

import type { SimulationResponse } from '../types';

interface ConversionCTAProps {
    result: SimulationResponse;
}

export const ConversionCTA: React.FC<ConversionCTAProps> = ({ result }) => {
    const currency = result.is_ucits ? 'EUR' : 'USD';
    const format = (val: number) =>
        new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency,
            maximumFractionDigits: 0,
        }).format(val);

    const finalYear = result.yearly_data[result.yearly_data.length - 1];

    // The devastating mathematical reality of passive investing:
    const sequenceRiskGap = result.final_median_value - finalYear.percentile_10;
    const inflationLoss = result.final_median_value - result.final_real_value;

    return (
        <div className="mt-8 bg-slate-900 rounded-2xl p-8 text-white shadow-xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-sky-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 -translate-y-1/2 translate-x-1/2"></div>

            <div className="relative z-10">
                <h3 className="text-2xl font-bold mb-6 tracking-tight">
                    How Much Future Wealth Are You Accidentally Leaving Behind?
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <div className="bg-slate-800/80 p-5 rounded-xl border border-slate-700">
                        <p className="text-slate-400 text-sm font-medium mb-1">
                            Potential Market Risk:
                        </p>
                        <p className="text-2xl font-bold text-rose-400">
                            -{format(sequenceRiskGap)}
                        </p>
                        <p className="text-xs text-slate-500 mt-2">
                            This is how unfavorable market conditions could impact your
                            long-term wealth.
                        </p>
                    </div>

                    <div className="bg-slate-800/80 p-5 rounded-xl border border-slate-700">
                        <p className="text-slate-400 text-sm font-medium mb-1">
                            Hidden Inflation Impact:
                        </p>
                        <p className="text-2xl font-bold text-amber-400">
                            -{format(inflationLoss)}
                        </p>
                        <p className="text-xs text-slate-500 mt-2">
                            This is how inflation may quietly reduce the real value of your
                            future portfolio.
                        </p>
                    </div>
                </div>

                <div className="flex flex-col md:flex-row gap-6 items-center">
                    <p className="text-slate-300 leading-relaxed flex-1 text-sm md:text-base">
                        A standard investment plan may expose you to unnecessary risk and
                        unpredictable outcomes. The{' '}
                        <strong className="text-white">Forge Ultra platform</strong> helps
                        you identify weaknesses, optimize your portfolio, and make informed
                        decisions based on data, not guesswork. Discover what your current
                        strategy could be missing. Upgrade to Premium and unlock advanced
                        portfolio intelligence today.
                    </p>
                    <a
                        href="https://www.ki-wealth.com/membership-levels"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full md:w-auto px-8 py-4 bg-sky-500 hover:bg-sky-400 text-slate-900 font-bold rounded-xl transition-colors shadow-lg shadow-sky-500/30 whitespace-nowrap text-center"
                    >
                        Subscribe and See Opportunities For Your Portfolio
                    </a>
                </div>
            </div>
        </div>
    );
};
